import { useState, useEffect, useMemo } from 'react'
import { Header } from '@/components/layout/Header'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/hooks/useAuth'
import {
  useGlobalSettings,
  useUpdateTelegramSettings,
  useUpdatePushoverSettings,
  useUpdateOrderbookDefaults,
  useUpdateVertexAISettings,
  useUpdateArkhamSettings,
  useUpdateStorageBoxSettings,
  useAuthUsers,
  useCreateAuthUser,
  useUpdateAuthUser,
  useDeleteAuthUser,
  useTradePatternSettings,
  useUpdateTradePatternSettings,
} from '@/hooks/useSettings'
import { MessageSquare, Bell, Settings2, Save, X, AlertTriangle, Users, Plus, Trash2, Edit2, Loader2, Sparkles, Shield, CloudUpload } from 'lucide-react'
import { unlinkAuthUserTelegram } from '@/lib/api'
import type { AuthUser, TelegramChannelConfig, TelegramTopicConfig } from '@/lib/api'
import type { TradePatternSettings } from '@/types'
import { TelegramDestinationSelect } from '@/components/settings/TelegramDestinationSelect'
import { MergeTelegramUserDialog } from '@/components/settings/MergeTelegramUserDialog'
import { normalizeTelegramDestinations, readTelegramTopicId } from '@/lib/telegramDestinations'

const PERMISSION_OPTIONS = [
  { key: 'alerts', label: 'OI' },
  { key: 'orderbook', label: 'Orderbook' },
  { key: 'mark_price', label: 'Mark Price' },
  { key: 'price_difference', label: 'Price Diff' },
  { key: 'tags', label: 'Tags & Aliases' },
  { key: 'manage_positions', label: 'Manage Positions' },
  { key: 'send_notification', label: 'Send Notifications' },
  { key: 'send_critical_alert', label: 'Send Critical Alerts' },
  { key: 'send_warning', label: 'Send Warning Alerts' },
]

const TRADE_PATTERN_ADVANCED_FIELDS: Array<[keyof TradePatternSettings, string, number]> = [
  ['periodic_min_intervals', 'Minimum periodic intervals', 1],
  ['periodic_min_seconds', 'Minimum periodic interval (seconds)', 1],
  ['periodic_max_seconds', 'Maximum periodic interval (seconds)', 1],
  ['periodic_min_match_ratio', 'Periodic match ratio', 0.01],
  ['timing_tolerance_seconds', 'Timing tolerance (seconds)', 0.1],
  ['timing_tolerance_ratio', 'Timing tolerance ratio', 0.01],
  ['max_missed_cycle_ratio', 'Maximum missed-cycle ratio', 0.01],
  ['burst_window_seconds', 'Burst window (seconds)', 1],
  ['burst_min_consecutive_windows', 'Consecutive burst windows', 1],
  ['burst_count_tolerance_ratio', 'Burst count tolerance', 0.01],
  ['sustained_after_minutes', 'Sustained after (minutes)', 1],
  ['inactive_confirmation_minutes', 'Inactive confirmation (minutes)', 1],
  ['allowed_lateness_seconds', 'Allowed lateness (seconds)', 1],
  ['max_candidates_per_stream', 'Candidate cap per stream', 1],
  ['candidate_ttl_minutes', 'Candidate TTL (minutes)', 1],
  ['max_active_patterns_per_symbol', 'Active pattern cap per symbol', 1],
  ['recent_event_limit', 'Recent event cap', 1],
  ['minute_bin_limit', 'Minute-bin cap', 1],
  ['active_json_max_bytes', 'Active state JSON cap (bytes)', 1],
  ['checkpoint_interval_seconds', 'Checkpoint interval (seconds)', 1],
  ['episode_minute_retention_days', 'Episode-minute retention (days)', 1],
  ['episode_history_retention_days', 'Episode history retention (days; 0 unlimited)', 1],
  ['correlation_grace_minutes', 'Correlation grace (minutes)', 1],
]

function parseTelegramNicknames(value: string): string[] {
  const seen = new Set<string>()
  return value
    .split(/[,\n]/)
    .map((item) => item.trim().replace(/^@+/, ''))
    .filter((item) => {
      if (!item) return false
      const key = item.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

function telegramNicknamesForUser(user: AuthUser): string[] {
  if (user.telegram_nicknames?.length) return user.telegram_nicknames
  return user.telegram_nickname ? [user.telegram_nickname] : []
}

function telegramChatIdsForUser(user: AuthUser): string[] {
  if (user.telegram_chat_ids?.length) return user.telegram_chat_ids
  return user.telegram_chat_id ? [user.telegram_chat_id] : []
}

function cloneTelegramChannels(channels?: Record<string, TelegramChannelConfig>): Record<string, TelegramChannelConfig> {
  return JSON.parse(JSON.stringify(channels || {}))
}

function uniqueTelegramKey(existing: Record<string, unknown>, base: string): string {
  let key = base
  let counter = 2
  while (Object.prototype.hasOwnProperty.call(existing, key)) {
    key = `${base}_${counter}`
    counter += 1
  }
  return key
}

function topicNameForEditor(topicKey: string, topic: TelegramTopicConfig | undefined): string {
  if (topic && typeof topic === 'object') {
    if ('name' in topic) return String(topic.name ?? '')
    if ('label' in topic) return String(topic.label ?? '')
  }
  return topicKey.replace(/_/g, ' ')
}

function topicForEditor(topicKey: string, topic: TelegramTopicConfig | undefined): Exclude<TelegramTopicConfig, string | number | null> {
  return {
    name: topicNameForEditor(topicKey, topic),
    topic_id: readTelegramTopicId(topic),
  }
}

function readBackupCompression(value: {
  compression?: 'zstd' | 'gzip' | 'none'
  compress?: boolean
} | undefined): 'zstd' | 'gzip' | 'none' {
  if (value?.compression) return value.compression
  if (value?.compress === true) return 'gzip'
  if (value?.compress === false) return 'none'
  return 'zstd'
}

export default function Settings() {
  const { isAdmin } = useAuth()
  const { data: settings, isLoading } = useGlobalSettings()
  const { data: tradePatternSettings } = useTradePatternSettings(isAdmin)
  const updateTradePatterns = useUpdateTradePatternSettings()
  const [tradePatternDraft, setTradePatternDraft] = useState<TradePatternSettings | null>(null)
  const [showTradePatternAdvanced, setShowTradePatternAdvanced] = useState(false)

  // Telegram state
  const [telegramBotToken, setTelegramBotToken] = useState('')
  const [telegramChatId, setTelegramChatId] = useState('')
  const [telegramReplyTo, setTelegramReplyTo] = useState('')
  const [positionDiscussionChatId, setPositionDiscussionChatId] = useState('')
  const [telegramWebappUrl, setTelegramWebappUrl] = useState('')
  const [telegramLoginClientId, setTelegramLoginClientId] = useState('')
  const [telegramLoginClientSecret, setTelegramLoginClientSecret] = useState('')
  const [telegramLoginRedirectUri, setTelegramLoginRedirectUri] = useState('')
  const [telegramInfoChat, setTelegramInfoChat] = useState('')
  const [telegramInfoTopicId, setTelegramInfoTopicId] = useState('')
  const [historyFeedChannelId, setHistoryFeedChannelId] = useState('')
  const [systemReportsEnabled, setSystemReportsEnabled] = useState(false)
  const [systemReportsChatId, setSystemReportsChatId] = useState('')
  const [systemReportsTopicId, setSystemReportsTopicId] = useState('')
  const [systemReportsDigestInterval, setSystemReportsDigestInterval] = useState('900')
  const [systemReportsMinLogLevel, setSystemReportsMinLogLevel] = useState('CRITICAL')
  const [systemReportsLogTailBytes, setSystemReportsLogTailBytes] = useState('4096')
  const [telegramChannels, setTelegramChannels] = useState<Record<string, TelegramChannelConfig>>({})

  // Pushover state
  const [pushoverAppToken, setPushoverAppToken] = useState('')
  const [pushoverGroupKey, setPushoverGroupKey] = useState('')

  // Orderbook defaults state
  const [orderbookNotificationPct, setOrderbookNotificationPct] = useState<number[]>([5])

  // Vertex AI / Gemini state
  const [vertexApiKey, setVertexApiKey] = useState('')
  const [vertexModel, setVertexModel] = useState('gemini-2.5-flash')

  // Arkham Intelligence state
  const [arkhamApiKey, setArkhamApiKey] = useState('')
  const [arkhamEnabled, setArkhamEnabled] = useState(true)
  const [arkhamMaxTransfers, setArkhamMaxTransfers] = useState<number[]>([50])
  const [arkhamMinUsd, setArkhamMinUsd] = useState('10000')

  // Storage Box state
  const [storageBoxEnabled, setStorageBoxEnabled] = useState(false)
  const [storageTransport, setStorageTransport] = useState<'local' | 'webdav'>('local')
  const [storageLocalPath, setStorageLocalPath] = useState('')
  const [storageWebdavUrl, setStorageWebdavUrl] = useState('')
  const [storageUsername, setStorageUsername] = useState('')
  const [storagePassword, setStoragePassword] = useState('')
  const [storageRemotePath, setStorageRemotePath] = useState('cparser-v3')
  const [storageCrashLogsPath, setStorageCrashLogsPath] = useState('crash-logs')
  const [storageTimeoutSeconds, setStorageTimeoutSeconds] = useState('15')
  const [storageVerifyTls, setStorageVerifyTls] = useState(true)
  const [storageCrashReportsEnabled, setStorageCrashReportsEnabled] = useState(true)
  const [storageLocalSpoolDir, setStorageLocalSpoolDir] = useState('cparser_v3/data/crash_logs')
  const [storageRecentLogBytes, setStorageRecentLogBytes] = useState('200000')
  const [storageDbBackupsEnabled, setStorageDbBackupsEnabled] = useState(false)
  const [storageDbBackupSpoolDir, setStorageDbBackupSpoolDir] = useState('cparser_v3/data/db_backups')
  const [storageDbBackupRemotePath, setStorageDbBackupRemotePath] = useState('db-backups')
  const [storageDbBackupIntervalSeconds, setStorageDbBackupIntervalSeconds] = useState('21600')
  const [storageDbBackupRunOnStartup, setStorageDbBackupRunOnStartup] = useState(true)
  const [storageDbBackupRetentionCount, setStorageDbBackupRetentionCount] = useState('0')
  const [storageDbBackupSnapshotMode, setStorageDbBackupSnapshotMode] = useState<'auto' | 'compact' | 'online'>('auto')
  const [storageDbBackupCompression, setStorageDbBackupCompression] = useState<'zstd' | 'gzip' | 'none'>('zstd')
  const [storageDbBackupCompressionLevel, setStorageDbBackupCompressionLevel] = useState('1')
  const [storageDbBackupProcessNice, setStorageDbBackupProcessNice] = useState('10')
  const [storageDbBackupCpuLimitPercent, setStorageDbBackupCpuLimitPercent] = useState('35')
  const [storageDbBackupIoPriority, setStorageDbBackupIoPriority] = useState<'idle' | 'best-effort' | 'unchanged'>('idle')

  // Mutations
  const updateTelegram = useUpdateTelegramSettings()
  const updatePushover = useUpdatePushoverSettings()
  const updateOrderbook = useUpdateOrderbookDefaults()
  const updateVertexAI = useUpdateVertexAISettings()
  const updateArkham = useUpdateArkhamSettings()
  const updateStorageBox = useUpdateStorageBoxSettings()

  // User management
  const { data: usersData, isLoading: usersLoading, refetch: refetchUsers } = useAuthUsers()
  const createUser = useCreateAuthUser()
  const updateUser = useUpdateAuthUser()
  const deleteUser = useDeleteAuthUser()

  // User management dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AuthUser | null>(null)
  const [newUsername, setNewUsername] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newRole, setNewRole] = useState<'user' | 'admin'>('user')
  const [newActive, setNewActive] = useState(true)
  const [newPushoverKey, setNewPushoverKey] = useState('')
  const [newTelegramNickname, setNewTelegramNickname] = useState('')
  const [newPermissions, setNewPermissions] = useState<Record<string, boolean>>({})

  // Success/error state
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // Load settings into state
  useEffect(() => {
    if (settings) {
      setTelegramBotToken('')
      setTelegramChatId(settings.telegram?.default_chat_id || '')
      setTelegramReplyTo(String(settings.telegram?.default_reply_to_message_id || ''))
      setPositionDiscussionChatId(settings.telegram?.position_discussion_chat_id || '')
      setTelegramWebappUrl(settings.telegram?.webapp_url || '')
      setTelegramLoginClientId(settings.telegram?.login_client_id || '')
      setTelegramLoginClientSecret('')
      setTelegramLoginRedirectUri(settings.telegram?.login_redirect_uri || '')
      setTelegramInfoChat(String(settings.telegram?.info_chat || ''))
      setTelegramInfoTopicId(String(settings.telegram?.info_topic_id || ''))
      setHistoryFeedChannelId(String(settings.telegram?.history_feed_channel_id || ''))
      setSystemReportsEnabled(!!settings.telegram?.system_reports?.enabled)
      setSystemReportsChatId(String(settings.telegram?.system_reports?.chat_id || ''))
      setSystemReportsTopicId(String(settings.telegram?.system_reports?.topic_id || ''))
      setSystemReportsDigestInterval(String(settings.telegram?.system_reports?.health_digest_interval_seconds || 900))
      setSystemReportsMinLogLevel(String(settings.telegram?.system_reports?.min_log_level || 'CRITICAL'))
      setSystemReportsLogTailBytes(String(settings.telegram?.system_reports?.include_log_tail_bytes || 4096))
      setTelegramChannels(cloneTelegramChannels(settings.notifiers?.telegram?.channels))
      setPushoverAppToken(settings.pushover?.app_token || '')
      setPushoverGroupKey(settings.pushover?.group_key || '')
      setOrderbookNotificationPct([settings.orderbook_settings?.default_update_notification_pct || 5])
      setVertexApiKey('')
      setVertexModel(settings.vertex_ai?.model || 'gemini-2.5-flash')
      setArkhamApiKey('')  // API key is masked, don't pre-fill
      setArkhamEnabled(settings.arkham?.enabled !== false)
      setArkhamMaxTransfers([settings.arkham?.max_transfers || 50])
      setArkhamMinUsd(String(settings.arkham?.min_transfer_usd || 10000))
      setStorageBoxEnabled(!!settings.storage_box?.enabled)
      setStorageTransport(settings.storage_box?.transport || 'local')
      setStorageLocalPath(settings.storage_box?.local_path || '')
      setStorageWebdavUrl(settings.storage_box?.webdav_url || '')
      setStorageUsername(settings.storage_box?.username || '')
      setStoragePassword('')
      setStorageRemotePath(settings.storage_box?.remote_path || 'cparser-v3')
      setStorageCrashLogsPath(settings.storage_box?.crash_logs_path || 'crash-logs')
      setStorageTimeoutSeconds(String(settings.storage_box?.timeout_seconds || 15))
      setStorageVerifyTls(settings.storage_box?.verify_tls !== false)
      setStorageCrashReportsEnabled(settings.storage_box?.crash_reports?.enabled !== false)
      setStorageLocalSpoolDir(settings.storage_box?.crash_reports?.local_spool_dir || 'cparser_v3/data/crash_logs')
      setStorageRecentLogBytes(String(settings.storage_box?.crash_reports?.include_recent_log_bytes || 200000))
      setStorageDbBackupsEnabled(!!settings.storage_box?.database_backups?.enabled)
      setStorageDbBackupSpoolDir(settings.storage_box?.database_backups?.local_spool_dir || 'cparser_v3/data/db_backups')
      setStorageDbBackupRemotePath(settings.storage_box?.database_backups?.remote_path || 'db-backups')
      setStorageDbBackupIntervalSeconds(String(settings.storage_box?.database_backups?.interval_seconds || 21600))
      setStorageDbBackupRunOnStartup(settings.storage_box?.database_backups?.run_on_startup !== false)
      setStorageDbBackupRetentionCount(String(settings.storage_box?.database_backups?.retention_count || 0))
      setStorageDbBackupSnapshotMode(settings.storage_box?.database_backups?.snapshot_mode || 'auto')
      setStorageDbBackupCompression(readBackupCompression(settings.storage_box?.database_backups))
      setStorageDbBackupCompressionLevel(String(settings.storage_box?.database_backups?.compression_level || 1))
      setStorageDbBackupProcessNice(String(settings.storage_box?.database_backups?.process_nice ?? 10))
      setStorageDbBackupCpuLimitPercent(String(settings.storage_box?.database_backups?.cpu_limit_percent ?? 35))
      setStorageDbBackupIoPriority(settings.storage_box?.database_backups?.io_priority || 'idle')
    }
  }, [settings])

  useEffect(() => {
    if (tradePatternSettings?.config) {
      setTradePatternDraft(tradePatternSettings.config)
    }
  }, [tradePatternSettings])

  const telegramDestinations = useMemo(
    () => normalizeTelegramDestinations(telegramChannels),
    [telegramChannels]
  )

  const addTelegramChannel = () => {
    setTelegramChannels((prev) => {
      const key = uniqueTelegramKey(prev, 'channel')
      return {
        ...prev,
        [key]: {
          name: 'New Channel',
          bot: 'main',
          chat_id: '',
          topics: {},
        },
      }
    })
  }

  const updateTelegramChannel = (channelKey: string, updates: Partial<TelegramChannelConfig>) => {
    setTelegramChannels((prev) => ({
      ...prev,
      [channelKey]: {
        ...(prev[channelKey] || {}),
        ...updates,
      },
    }))
  }

  const removeTelegramChannel = (channelKey: string) => {
    setTelegramChannels((prev) => {
      const next = { ...prev }
      delete next[channelKey]
      return next
    })
  }

  const addTelegramTopic = (channelKey: string) => {
    setTelegramChannels((prev) => {
      const channel = prev[channelKey] || {}
      const topics = channel.topics || {}
      const topicKey = uniqueTelegramKey(topics, 'topic')
      return {
        ...prev,
        [channelKey]: {
          ...channel,
          topics: {
            ...topics,
            [topicKey]: {
              name: 'New Topic',
              topic_id: '',
            },
          },
        },
      }
    })
  }

  const updateTelegramTopic = (
    channelKey: string,
    topicKey: string,
    updates: Exclude<TelegramTopicConfig, string | number | null>
  ) => {
    setTelegramChannels((prev) => {
      const channel = prev[channelKey] || {}
      const topics = channel.topics || {}
      const topic = topicForEditor(topicKey, topics[topicKey])
      return {
        ...prev,
        [channelKey]: {
          ...channel,
          topics: {
            ...topics,
            [topicKey]: {
              ...topic,
              ...updates,
            },
          },
        },
      }
    })
  }

  const removeTelegramTopic = (channelKey: string, topicKey: string) => {
    setTelegramChannels((prev) => {
      const channel = prev[channelKey] || {}
      const topics = { ...(channel.topics || {}) }
      delete topics[topicKey]
      return {
        ...prev,
        [channelKey]: {
          ...channel,
          topics,
        },
      }
    })
  }

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg)
    setErrorMessage('')
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  const showError = (msg: string) => {
    setErrorMessage(msg)
    setSuccessMessage('')
    setTimeout(() => setErrorMessage(''), 5000)
  }

  const handleSaveTelegram = async () => {
    try {
      const payload = {
        default_chat_id: telegramChatId,
        default_reply_to_message_id: telegramReplyTo || undefined,
        position_discussion_chat_id: positionDiscussionChatId || undefined,
        webapp_url: telegramWebappUrl || undefined,
        login_client_id: telegramLoginClientId,
        login_redirect_uri: telegramLoginRedirectUri,
        info_chat: telegramInfoChat || undefined,
        info_topic_id: telegramInfoTopicId || undefined,
        history_feed_channel_id: historyFeedChannelId || undefined,
        system_reports: {
          enabled: systemReportsEnabled,
          chat_id: systemReportsChatId || undefined,
          topic_id: systemReportsTopicId || undefined,
          health_digest_interval_seconds: parseInt(systemReportsDigestInterval, 10) || 900,
          min_log_level: systemReportsMinLogLevel || 'CRITICAL',
          include_log_tail_bytes: parseInt(systemReportsLogTailBytes, 10) || 4096,
        },
        channels: telegramChannels,
        ...(telegramBotToken.trim() ? { bot_token: telegramBotToken.trim() } : {}),
        ...(telegramLoginClientSecret.trim()
          ? { login_client_secret: telegramLoginClientSecret.trim() }
          : {}),
      }
      await updateTelegram.mutateAsync(payload)
      setTelegramBotToken('')
      setTelegramLoginClientSecret('')
      showSuccess('Telegram settings saved')
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to save Telegram settings')
    }
  }

  const handleAdminUnlinkTelegram = async (user: AuthUser, telegramUserId: string) => {
    try {
      const result = await unlinkAuthUserTelegram(user.username, telegramUserId)
      if (!result.ok) {
        showError(result.error || 'Failed to unlink Telegram account')
        return
      }
      if (result.user) setSelectedUser(result.user)
      await refetchUsers()
      showSuccess('Telegram account unlinked')
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to unlink Telegram account')
    }
  }

  const handleSavePushover = async () => {
    try {
      await updatePushover.mutateAsync({
        app_token: pushoverAppToken,
        group_key: pushoverGroupKey,
      })
      showSuccess('Pushover settings saved')
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to save Pushover settings')
    }
  }

  const handleSaveOrderbookDefaults = async () => {
    try {
      await updateOrderbook.mutateAsync({
        default_update_notification_pct: orderbookNotificationPct[0],
      })
      showSuccess('Orderbook defaults saved')
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to save orderbook defaults')
    }
  }

  const handleTradePatternProfile = (profile: TradePatternSettings['profile']) => {
    const preset = tradePatternSettings?.presets?.[profile]
    if (preset) setTradePatternDraft({ ...preset, enabled: tradePatternDraft?.enabled ?? true })
  }

  const handleSaveTradePatterns = async () => {
    if (!tradePatternDraft) return
    try {
      await updateTradePatterns.mutateAsync({
        profile: tradePatternDraft.profile,
        config: tradePatternDraft,
      })
      showSuccess('Temporal trade-pattern settings saved')
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to save trade-pattern settings')
    }
  }

  const handleSaveVertexAI = async () => {
    try {
      const payload: { api_key?: string; model?: string } = {
        model: vertexModel,
      }
      if (vertexApiKey.trim()) {
        payload.api_key = vertexApiKey
      }
      await updateVertexAI.mutateAsync(payload)
      showSuccess('Gemini AI settings saved')
      setVertexApiKey('')
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to save Gemini settings')
    }
  }

  const handleSaveArkham = async () => {
    try {
      const payload: Record<string, unknown> = {
        enabled: arkhamEnabled,
        max_transfers: arkhamMaxTransfers[0],
        min_transfer_usd: parseInt(arkhamMinUsd) || 10000,
      }
      // Only send api_key if user typed a new one
      if (arkhamApiKey.trim()) {
        payload.api_key = arkhamApiKey
      }
      await updateArkham.mutateAsync(payload as Parameters<typeof updateArkham.mutateAsync>[0])
      showSuccess('Arkham Intelligence settings saved')
      setArkhamApiKey('')  // Clear after save
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to save Arkham settings')
    }
  }

  const handleSaveStorageBox = async () => {
    try {
      const cpuLimitPercent = Number(storageDbBackupCpuLimitPercent)
      if (!storageDbBackupCpuLimitPercent.trim() || !Number.isInteger(cpuLimitPercent) || cpuLimitPercent < 0 || cpuLimitPercent > 100) {
        throw new Error('Backup CPU target must be a whole number from 0 to 100')
      }
      const payload = {
        enabled: storageBoxEnabled,
        transport: storageTransport,
        local_path: storageLocalPath,
        webdav_url: storageWebdavUrl,
        username: storageUsername,
        remote_path: storageRemotePath,
        crash_logs_path: storageCrashLogsPath,
        timeout_seconds: parseInt(storageTimeoutSeconds, 10) || 15,
        verify_tls: storageVerifyTls,
        crash_reports: {
          enabled: storageCrashReportsEnabled,
          local_spool_dir: storageLocalSpoolDir,
          include_recent_log_bytes: parseInt(storageRecentLogBytes, 10) || 0,
        },
        database_backups: {
          enabled: storageDbBackupsEnabled,
          local_spool_dir: storageDbBackupSpoolDir,
          remote_path: storageDbBackupRemotePath,
          interval_seconds: parseInt(storageDbBackupIntervalSeconds, 10) || 21600,
          run_on_startup: storageDbBackupRunOnStartup,
          retention_count: parseInt(storageDbBackupRetentionCount, 10) || 0,
          snapshot_mode: storageDbBackupSnapshotMode,
          compression: storageDbBackupCompression,
          compression_level: parseInt(storageDbBackupCompressionLevel, 10) || 1,
          process_nice: parseInt(storageDbBackupProcessNice, 10) || 0,
          cpu_limit_percent: cpuLimitPercent,
          io_priority: storageDbBackupIoPriority,
          compress: storageDbBackupCompression !== 'none',
        },
        ...(storagePassword.trim() ? { password: storagePassword } : {}),
      }
      await updateStorageBox.mutateAsync(payload)
      showSuccess('Storage Box settings saved')
      setStoragePassword('')
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to save Storage Box settings')
    }
  }

  // User management handlers
  const resetUserForm = () => {
    setNewUsername('')
    setNewPassword('')
    setNewRole('user')
    setNewActive(true)
    setNewPushoverKey('')
    setNewTelegramNickname('')
    setNewPermissions({})
    setSelectedUser(null)
  }

  const handleOpenCreateDialog = () => {
    resetUserForm()
    setIsCreateDialogOpen(true)
  }

  const handleOpenEditDialog = (user: AuthUser) => {
    setSelectedUser(user)
    setNewUsername(user.username)
    setNewPassword('')
    setNewRole(user.role)
    setNewActive(user.active)
    setNewPushoverKey(user.pushover_user_key || '')
    setNewTelegramNickname(telegramNicknamesForUser(user).join('\n'))
    setNewPermissions(user.permissions || {})
    setIsEditDialogOpen(true)
  }

  const handleOpenDeleteDialog = (user: AuthUser) => {
    setSelectedUser(user)
    setIsDeleteDialogOpen(true)
  }

  const handleCreateUser = async () => {
    if (!newUsername.trim() || !newPassword.trim()) {
      showError('Username and password are required')
      return
    }
    try {
      const permissions = newRole === 'admin' ? {} : newPermissions
      const result = await createUser.mutateAsync({
        username: newUsername.trim(),
        password: newPassword,
        role: newRole,
        active: newActive,
        permissions,
        pushover_user_key: newPushoverKey || undefined,
        telegram_nicknames: parseTelegramNicknames(newTelegramNickname),
      })
      if (result.ok) {
        showSuccess(`User "${newUsername}" created successfully`)
        setIsCreateDialogOpen(false)
        resetUserForm()
      } else {
        showError(result.error || 'Failed to create user')
      }
    } catch {
      showError('Failed to create user')
    }
  }

  const handleUpdateUser = async () => {
    if (!selectedUser) return
    try {
      const permissions = newRole === 'admin' ? {} : newPermissions
      const result = await updateUser.mutateAsync({
        username: selectedUser.username,
        updates: {
          password: newPassword || undefined,
          role: newRole,
          active: newActive,
          permissions,
          pushover_user_key: newPushoverKey || undefined,
          telegram_nicknames: parseTelegramNicknames(newTelegramNickname),
        },
      })
      if (result.ok) {
        showSuccess(`User "${selectedUser.username}" updated successfully`)
        setIsEditDialogOpen(false)
        resetUserForm()
      } else {
        showError(result.error || 'Failed to update user')
      }
    } catch {
      showError('Failed to update user')
    }
  }

  const handleDeleteUser = async () => {
    if (!selectedUser) return
    try {
      const result = await deleteUser.mutateAsync(selectedUser.username)
      if (result.ok) {
        showSuccess(`User "${selectedUser.username}" deleted successfully`)
        setIsDeleteDialogOpen(false)
        resetUserForm()
      } else {
        showError(result.error || 'Failed to delete user')
      }
    } catch {
      showError('Failed to delete user')
    }
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col">
        <Header title="Settings" />
        <div className="flex-1 flex items-center justify-center">
          <Card className="w-96">
            <CardContent className="py-8 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
              <p className="text-muted-foreground">
                You don't have permission to access settings.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex flex-col">
        <Header title="Settings" />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <Header title="Settings" />

      <div className="flex-1 space-y-6 p-6">
        {/* Status messages */}
        {successMessage && (
          <div className="bg-green-500/10 text-green-500 text-sm p-3 rounded-md flex items-center justify-between">
            {successMessage}
            <Button variant="ghost" size="sm" onClick={() => setSuccessMessage('')}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        {errorMessage && (
          <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md flex items-center justify-between">
            {errorMessage}
            <Button variant="ghost" size="sm" onClick={() => setErrorMessage('')}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Telegram Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Telegram Settings
            </CardTitle>
            <CardDescription>
              Configure Telegram bot for notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="telegram-token">Bot Token</Label>
                <Input
                  id="telegram-token"
                  type="password"
                  placeholder={settings?.telegram?.bot_token_masked || 'Enter bot token'}
                  value={telegramBotToken}
                  onChange={(e) => setTelegramBotToken(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telegram-webapp-url">Web App URL</Label>
                <Input
                  id="telegram-webapp-url"
                  placeholder="https://example.com"
                  value={telegramWebappUrl}
                  onChange={(e) => setTelegramWebappUrl(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-3 rounded-lg border p-3">
              <div>
                <Label>Telegram Web Login (OIDC)</Label>
                <p className="text-xs text-muted-foreground">
                  Create Web Login credentials in BotFather. New Telegram users are provisioned with no permissions.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="telegram-login-client-id">Client ID</Label>
                  <Input
                    id="telegram-login-client-id"
                    value={telegramLoginClientId}
                    onChange={(e) => setTelegramLoginClientId(e.target.value)}
                    placeholder="BotFather Web Login client ID"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telegram-login-client-secret">Client Secret</Label>
                  <Input
                    id="telegram-login-client-secret"
                    type="password"
                    value={telegramLoginClientSecret}
                    onChange={(e) => setTelegramLoginClientSecret(e.target.value)}
                    placeholder={settings?.telegram?.login_client_secret_masked || 'BotFather client secret'}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="telegram-login-redirect-uri">Callback URL override (optional)</Label>
                <Input
                  id="telegram-login-redirect-uri"
                  value={telegramLoginRedirectUri}
                  onChange={(e) => setTelegramLoginRedirectUri(e.target.value)}
                  placeholder={`${telegramWebappUrl || 'https://example.com'}/api/auth/telegram/callback`}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to derive it from the Web App URL. Add the exact callback URL to BotFather Allowed URLs.
                </p>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label>Telegram Destinations</Label>
                  <p className="text-xs text-muted-foreground">
                    Preconfigured channels and topics used by alert dropdowns
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addTelegramChannel}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Channel
                </Button>
              </div>

              {Object.entries(telegramChannels).length === 0 ? (
                <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  No Telegram destinations configured
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(telegramChannels).map(([channelKey, channel]) => (
                    <div key={channelKey} className="space-y-3 rounded-md border p-3">
                      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Channel Name</Label>
                          <Input
                            value={String(channel.name || channelKey.replace(/_/g, ' '))}
                            onChange={(e) => updateTelegramChannel(channelKey, { name: e.target.value })}
                            placeholder="Main alerts"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Chat ID</Label>
                          <Input
                            value={String(channel.chat_id || '')}
                            onChange={(e) => updateTelegramChannel(channelKey, { chat_id: e.target.value })}
                            placeholder="-100..."
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeTelegramChannel(channelKey)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">Topics</Label>
                          <Button type="button" variant="outline" size="sm" onClick={() => addTelegramTopic(channelKey)}>
                            <Plus className="mr-2 h-3 w-3" />
                            Add Topic
                          </Button>
                        </div>
                        {Object.entries(channel.topics || {}).map(([topicKey, topicConfig]) => {
                          const topic = topicForEditor(topicKey, topicConfig)
                          return (
                            <div key={topicKey} className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                              <Input
                                value={String(topic.name || '')}
                                onChange={(e) => updateTelegramTopic(channelKey, topicKey, { name: e.target.value })}
                                placeholder="Topic name"
                              />
                              <Input
                                value={String(topic.topic_id || '')}
                                onChange={(e) => updateTelegramTopic(channelKey, topicKey, { topic_id: e.target.value })}
                                placeholder="Topic/message ID"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeTelegramTopic(channelKey, topicKey)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="telegram-default-destination">Default Destination</Label>
                <TelegramDestinationSelect
                  id="telegram-default-destination"
                  destinations={telegramDestinations}
                  chatId={telegramChatId}
                  replyToMessageId={telegramReplyTo}
                  onChange={({ chat_id, reply_to_message_id }) => {
                    setTelegramChatId(chat_id)
                    setTelegramReplyTo(reply_to_message_id ? String(reply_to_message_id) : '')
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telegram-position-destination">Position Discussion Channel</Label>
                <TelegramDestinationSelect
                  id="telegram-position-destination"
                  destinations={telegramDestinations}
                  filter="channels"
                  emptyLabel="No position discussion channel"
                  chatId={positionDiscussionChatId}
                  onChange={({ chat_id }) => setPositionDiscussionChatId(chat_id)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telegram-info-destination">Info Destination</Label>
                <TelegramDestinationSelect
                  id="telegram-info-destination"
                  destinations={telegramDestinations}
                  emptyLabel="No info destination"
                  chatId={telegramInfoChat}
                  replyToMessageId={telegramInfoTopicId}
                  onChange={({ chat_id, reply_to_message_id }) => {
                    setTelegramInfoChat(chat_id)
                    setTelegramInfoTopicId(reply_to_message_id ? String(reply_to_message_id) : '')
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telegram-history-feed-destination">History Feed Channel</Label>
                <TelegramDestinationSelect
                  id="telegram-history-feed-destination"
                  destinations={telegramDestinations}
                  filter="channels"
                  emptyLabel="No history feed channel"
                  chatId={historyFeedChannelId}
                  onChange={({ chat_id }) => {
                    setHistoryFeedChannelId(chat_id)
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Separate channel for position lifecycle events &amp; history updates
                </p>
              </div>
            </div>
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <Label htmlFor="telegram-system-reports-enabled">System Reports</Label>
                <Switch
                  id="telegram-system-reports-enabled"
                  checked={systemReportsEnabled}
                  onCheckedChange={setSystemReportsEnabled}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="telegram-system-reports-destination">System Reports Destination</Label>
                  <TelegramDestinationSelect
                    id="telegram-system-reports-destination"
                    destinations={telegramDestinations}
                    emptyLabel="Use info destination"
                    chatId={systemReportsChatId}
                    replyToMessageId={systemReportsTopicId}
                    onChange={({ chat_id, reply_to_message_id }) => {
                      setSystemReportsChatId(chat_id)
                      setSystemReportsTopicId(reply_to_message_id ? String(reply_to_message_id) : '')
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telegram-system-reports-min-level">Minimum Log Level</Label>
                  <Select value={systemReportsMinLogLevel} onValueChange={setSystemReportsMinLogLevel}>
                    <SelectTrigger id="telegram-system-reports-min-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CRITICAL">Critical</SelectItem>
                      <SelectItem value="ERROR">Error</SelectItem>
                      <SelectItem value="WARNING">Warning</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telegram-system-reports-digest-interval">Health Digest Interval Seconds</Label>
                  <Input
                    id="telegram-system-reports-digest-interval"
                    type="number"
                    min="60"
                    value={systemReportsDigestInterval}
                    onChange={(event) => setSystemReportsDigestInterval(event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telegram-system-reports-tail-bytes">Log Tail Bytes</Label>
                  <Input
                    id="telegram-system-reports-tail-bytes"
                    type="number"
                    min="0"
                    value={systemReportsLogTailBytes}
                    onChange={(event) => setSystemReportsLogTailBytes(event.target.value)}
                  />
                </div>
              </div>
            </div>
            <Button onClick={handleSaveTelegram} disabled={updateTelegram.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {updateTelegram.isPending ? 'Saving...' : 'Save Telegram Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Pushover Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Pushover Settings
            </CardTitle>
            <CardDescription>
              Configure Pushover for mobile push notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pushover-app-token">App Token</Label>
                <Input
                  id="pushover-app-token"
                  type="password"
                  placeholder="Your Pushover application token"
                  value={pushoverAppToken}
                  onChange={(e) => setPushoverAppToken(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Create an application at pushover.net/apps
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pushover-group">Group Key (for /notify_all_users)</Label>
                <Input
                  id="pushover-group"
                  placeholder="Pushover delivery group key"
                  value={pushoverGroupKey}
                  onChange={(e) => setPushoverGroupKey(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Used by the Telegram bot to broadcast to all users at once
                </p>
              </div>
            </div>
            <Button onClick={handleSavePushover} disabled={updatePushover.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {updatePushover.isPending ? 'Saving...' : 'Save Pushover Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Storage Box */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CloudUpload className="h-5 w-5" />
              Storage Box
            </CardTitle>
            <CardDescription>
              Crash reports and remote diagnostics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Storage Box Uploads</Label>
                <p className="text-xs text-muted-foreground">
                  Copies crash reports to the configured remote target
                </p>
              </div>
              <Switch
                checked={storageBoxEnabled}
                onCheckedChange={setStorageBoxEnabled}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="storage-transport">Transport</Label>
                <Select value={storageTransport} onValueChange={(value) => setStorageTransport(value as 'local' | 'webdav')}>
                  <SelectTrigger id="storage-transport">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="local">Mounted Path</SelectItem>
                    <SelectItem value="webdav">WebDAV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="storage-timeout">Timeout Seconds</Label>
                <Input
                  id="storage-timeout"
                  inputMode="numeric"
                  value={storageTimeoutSeconds}
                  onChange={(e) => setStorageTimeoutSeconds(e.target.value)}
                />
              </div>
            </div>

            {storageTransport === 'local' ? (
              <div className="space-y-2">
                <Label htmlFor="storage-local-path">Mounted Storage Box Path</Label>
                <Input
                  id="storage-local-path"
                  placeholder="/mnt/storagebox"
                  value={storageLocalPath}
                  onChange={(e) => setStorageLocalPath(e.target.value)}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="storage-webdav-url">WebDAV URL</Label>
                    <Input
                      id="storage-webdav-url"
                      placeholder="https://user.your-storagebox.de"
                      value={storageWebdavUrl}
                      onChange={(e) => setStorageWebdavUrl(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="storage-username">Username</Label>
                    <Input
                      id="storage-username"
                      value={storageUsername}
                      onChange={(e) => setStorageUsername(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="storage-password">Password</Label>
                    <Input
                      id="storage-password"
                      type="password"
                      placeholder={settings?.storage_box?.password_masked || 'Storage Box password'}
                      value={storagePassword}
                      onChange={(e) => setStoragePassword(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <Label htmlFor="storage-verify-tls">Verify TLS</Label>
                    <Switch
                      id="storage-verify-tls"
                      checked={storageVerifyTls}
                      onCheckedChange={setStorageVerifyTls}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="storage-remote-path">Remote Root</Label>
                <Input
                  id="storage-remote-path"
                  placeholder="cparser-v3"
                  value={storageRemotePath}
                  onChange={(e) => setStorageRemotePath(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="storage-crash-path">Crash Reports Folder</Label>
                <Input
                  id="storage-crash-path"
                  placeholder="crash-logs"
                  value={storageCrashLogsPath}
                  onChange={(e) => setStorageCrashLogsPath(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-3 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <Label>Crash Reports</Label>
                <Switch
                  checked={storageCrashReportsEnabled}
                  onCheckedChange={setStorageCrashReportsEnabled}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="storage-spool-dir">Local Spool Directory</Label>
                  <Input
                    id="storage-spool-dir"
                    value={storageLocalSpoolDir}
                    onChange={(e) => setStorageLocalSpoolDir(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="storage-log-tail">Recent Log Bytes</Label>
                  <Input
                    id="storage-log-tail"
                    inputMode="numeric"
                    value={storageRecentLogBytes}
                    onChange={(e) => setStorageRecentLogBytes(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <Label>Database Backups</Label>
                <Switch
                  checked={storageDbBackupsEnabled}
                  onCheckedChange={setStorageDbBackupsEnabled}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="storage-db-backup-spool-dir">Local Backup Directory</Label>
                  <Input
                    id="storage-db-backup-spool-dir"
                    value={storageDbBackupSpoolDir}
                    onChange={(e) => setStorageDbBackupSpoolDir(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="storage-db-backup-remote-path">Remote Backup Folder</Label>
                  <Input
                    id="storage-db-backup-remote-path"
                    value={storageDbBackupRemotePath}
                    onChange={(e) => setStorageDbBackupRemotePath(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="storage-db-backup-interval">Interval Seconds</Label>
                  <Input
                    id="storage-db-backup-interval"
                    inputMode="numeric"
                    value={storageDbBackupIntervalSeconds}
                    onChange={(e) => setStorageDbBackupIntervalSeconds(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="storage-db-backup-retention">Local Retention Count</Label>
                  <Input
                    id="storage-db-backup-retention"
                    inputMode="numeric"
                    value={storageDbBackupRetentionCount}
                    onChange={(e) => setStorageDbBackupRetentionCount(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    0 keeps local backups indefinitely; remote retention is managed separately.
                  </p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="storage-db-backup-snapshot-mode">Snapshot Strategy</Label>
                  <Select
                    value={storageDbBackupSnapshotMode}
                    onValueChange={(value) => setStorageDbBackupSnapshotMode(value as 'auto' | 'compact' | 'online')}
                  >
                    <SelectTrigger id="storage-db-backup-snapshot-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (recommended)</SelectItem>
                      <SelectItem value="compact">Always compact</SelectItem>
                      <SelectItem value="online">Online copy</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Auto compacts databases with at least 25% reusable pages while preserving all live rows.
                    {' '}Choose Online copy to avoid extra compaction and verification passes when responsiveness matters most.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="storage-db-backup-compression">Compression</Label>
                  <Select
                    value={storageDbBackupCompression}
                    onValueChange={(value) => setStorageDbBackupCompression(value as 'zstd' | 'gzip' | 'none')}
                  >
                    <SelectTrigger id="storage-db-backup-compression">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zstd">Zstandard (recommended)</SelectItem>
                      <SelectItem value="gzip">Gzip compatibility</SelectItem>
                      <SelectItem value="none">No compression</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="storage-db-backup-compression-level">Compression Level (1–9)</Label>
                  <Input
                    id="storage-db-backup-compression-level"
                    inputMode="numeric"
                    disabled={storageDbBackupCompression === 'none'}
                    value={storageDbBackupCompressionLevel}
                    onChange={(e) => setStorageDbBackupCompressionLevel(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="storage-db-backup-process-nice">Process Nice Increment (0–19)</Label>
                  <Input
                    id="storage-db-backup-process-nice"
                    inputMode="numeric"
                    value={storageDbBackupProcessNice}
                    onChange={(e) => setStorageDbBackupProcessNice(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Higher values lower the backup child&apos;s CPU priority.</p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="storage-db-backup-cpu-limit">CPU Target After Live Copy (% of One Core)</Label>
                  <Input
                    id="storage-db-backup-cpu-limit"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={100}
                    step={1}
                    value={storageDbBackupCpuLimitPercent}
                    onChange={(e) => setStorageDbBackupCpuLimitPercent(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Default 35% average for compaction, verification, compression and upload; brief peaks are possible.
                    {' '}The live SQLite copy runs without CPU pacing to avoid repeated restarts during ingestion.
                    {' '}Lower values extend backup duration; 0 disables pacing.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="storage-db-backup-io-priority">Disk I/O Priority</Label>
                  <Select
                    value={storageDbBackupIoPriority}
                    onValueChange={(value) => setStorageDbBackupIoPriority(value as 'idle' | 'best-effort' | 'unchanged')}
                  >
                    <SelectTrigger id="storage-db-backup-io-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="idle">Idle (recommended)</SelectItem>
                      <SelectItem value="best-effort">Best effort (lowest priority)</SelectItem>
                      <SelectItem value="unchanged">Keep inherited priority</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Idle lets other disk work go first. Applies on Linux; the disk scheduler determines its effect.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <Label htmlFor="storage-db-backup-run-startup">Run On Startup</Label>
                <Switch
                  id="storage-db-backup-run-startup"
                  checked={storageDbBackupRunOnStartup}
                  onCheckedChange={setStorageDbBackupRunOnStartup}
                />
              </div>
            </div>

            <Button onClick={handleSaveStorageBox} disabled={updateStorageBox.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {updateStorageBox.isPending ? 'Saving...' : 'Save Storage Box'}
            </Button>
          </CardContent>
        </Card>

        {/* Temporal Trade Patterns */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Temporal Trade Patterns
            </CardTitle>
            <CardDescription>
              Detect recurring execution cadence, bursts, and sustained trade-size episodes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!tradePatternDraft ? (
              <p className="text-sm text-muted-foreground">Loading temporal pattern settings…</p>
            ) : (
              <>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <Label htmlFor="trade-pattern-enabled">Enable Temporal Detection</Label>
                    <p className="text-xs text-muted-foreground">Changes recycle the supervised trade worker.</p>
                  </div>
                  <Switch
                    id="trade-pattern-enabled"
                    checked={tradePatternDraft.enabled}
                    onCheckedChange={(enabled) => setTradePatternDraft({ ...tradePatternDraft, enabled })}
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="trade-pattern-profile">Detection Profile</Label>
                    <Select
                      value={tradePatternDraft.profile}
                      onValueChange={(value) => handleTradePatternProfile(value as TradePatternSettings['profile'])}
                    >
                      <SelectTrigger id="trade-pattern-profile"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conservative">Conservative</SelectItem>
                        <SelectItem value="balanced">Balanced (recommended)</SelectItem>
                        <SelectItem value="sensitive">Sensitive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="trade-pattern-horizons">Evaluation Horizons (minutes)</Label>
                    <Input
                      id="trade-pattern-horizons"
                      value={tradePatternDraft.evaluation_horizons_minutes.join(', ')}
                      onChange={(event) => setTradePatternDraft({
                        ...tradePatternDraft,
                        evaluation_horizons_minutes: event.target.value
                          .split(',')
                          .map((value) => Number(value.trim()))
                          .filter((value) => Number.isFinite(value) && value > 0),
                      })}
                    />
                  </div>
                  {([
                    ['min_trade_notional_usdt', 'Minimum trade notional (USDT)', 1],
                    ['notional_bucket_usdt', 'Notional bucket (USDT)', 1],
                    ['quantity_decimals', 'Quantity decimals', 1],
                    ['periodic_min_occurrences', 'Minimum periodic occurrences', 1],
                    ['burst_min_events', 'Minimum burst events', 1],
                    ['min_confidence', 'Minimum confidence', 0.01],
                  ] as Array<[keyof TradePatternSettings, string, number]>).map(([key, label, step]) => (
                    <div className="space-y-2" key={key}>
                      <Label htmlFor={`trade-pattern-${key}`}>{label}</Label>
                      <Input
                        id={`trade-pattern-${key}`}
                        type="number"
                        min="0"
                        step={step}
                        value={String(tradePatternDraft[key])}
                        onChange={(event) => setTradePatternDraft({
                          ...tradePatternDraft,
                          [key]: Number(event.target.value),
                        })}
                      />
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTradePatternAdvanced((value) => !value)}
                >
                  {showTradePatternAdvanced ? 'Hide Advanced Controls' : 'Show Advanced Controls'}
                </Button>
                {showTradePatternAdvanced && (
                  <div className="grid gap-4 rounded-md border p-4 md:grid-cols-2">
                    {TRADE_PATTERN_ADVANCED_FIELDS.map(([key, label, step]) => (
                      <div className="space-y-2" key={key}>
                        <Label htmlFor={`trade-pattern-${key}`}>{label}</Label>
                        <Input
                          id={`trade-pattern-${key}`}
                          type="number"
                          min="0"
                          step={step}
                          value={String(tradePatternDraft[key])}
                          onChange={(event) => setTradePatternDraft({
                            ...tradePatternDraft,
                            [key]: Number(event.target.value),
                          })}
                        />
                      </div>
                    ))}
                  </div>
                )}
                <Button onClick={handleSaveTradePatterns} disabled={updateTradePatterns.isPending}>
                  <Save className="mr-2 h-4 w-4" />
                  {updateTradePatterns.isPending ? 'Saving...' : 'Save Trade Pattern Settings'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Orderbook Defaults */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Orderbook Defaults
            </CardTitle>
            <CardDescription>
              Default settings for orderbook monitoring
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Default Update Notification Threshold</Label>
                  <span className="text-sm font-medium">{orderbookNotificationPct[0]}%</span>
                </div>
                <Slider
                  value={orderbookNotificationPct}
                  onValueChange={setOrderbookNotificationPct}
                  max={50}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Notify when order size changes by this percentage
                </p>
              </div>
            </div>
            <Button onClick={handleSaveOrderbookDefaults} disabled={updateOrderbook.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {updateOrderbook.isPending ? 'Saving...' : 'Save Defaults'}
            </Button>
          </CardContent>
        </Card>

        {/* Gemini AI Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Gemini AI Settings
            </CardTitle>
            <CardDescription>
              Configure Google Gemini AI for automatic ticker research
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="gemini-api-key">API Key</Label>
                <Input
                  id="gemini-api-key"
                  type="password"
                  placeholder={settings?.vertex_ai?.api_key_masked || 'Enter Gemini API key'}
                  value={vertexApiKey}
                  onChange={(e) => setVertexApiKey(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Get your API key from aistudio.google.com
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gemini-model">Model</Label>
                <Input
                  id="gemini-model"
                  placeholder="gemini-2.0-flash"
                  value={vertexModel}
                  onChange={(e) => setVertexModel(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Model name (e.g. gemini-2.0-flash, gemini-1.5-pro)
                </p>
              </div>
            </div>
            <Button onClick={handleSaveVertexAI} disabled={updateVertexAI.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {updateVertexAI.isPending ? 'Saving...' : 'Save Gemini Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Arkham Intelligence Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Arkham Intelligence
            </CardTitle>
            <CardDescription>
              On-chain wallet attribution, transfer tracking, and holder analysis
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Arkham Integration</Label>
                <p className="text-xs text-muted-foreground">
                  Enrich /analyze and /dex with wallet intelligence
                </p>
              </div>
              <Switch
                checked={arkhamEnabled}
                onCheckedChange={setArkhamEnabled}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="arkham-api-key">API Key</Label>
                <Input
                  id="arkham-api-key"
                  type="password"
                  placeholder={settings?.arkham?.api_key_masked || 'Enter Arkham API key'}
                  value={arkhamApiKey}
                  onChange={(e) => setArkhamApiKey(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Get your key from intel.arkm.com/api
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="arkham-min-usd">Min Transfer USD</Label>
                <Input
                  id="arkham-min-usd"
                  placeholder="10000"
                  value={arkhamMinUsd}
                  onChange={(e) => setArkhamMinUsd(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Minimum USD value for tracking transfers
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Max Transfers per Call</Label>
                <span className="text-sm font-medium">{arkhamMaxTransfers[0]}</span>
              </div>
              <Slider
                value={arkhamMaxTransfers}
                onValueChange={setArkhamMaxTransfers}
                max={100}
                min={10}
                step={10}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Credits cost: 2 per transfer row. {arkhamMaxTransfers[0]} transfers = {arkhamMaxTransfers[0] * 2} credits
              </p>
            </div>
            <Button onClick={handleSaveArkham} disabled={updateArkham.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {updateArkham.isPending ? 'Saving...' : 'Save Arkham Settings'}
            </Button>
          </CardContent>
        </Card>

        <Separator />

        {/* User Management */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  User Management
                </CardTitle>
                <CardDescription>
                  Create, edit, merge Telegram duplicates, and delete users
                </CardDescription>
              </div>
              <Button onClick={handleOpenCreateDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {usersLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !usersData?.users?.length ? (
              <div className="text-center py-8 text-muted-foreground">
                No users found
              </div>
            ) : (
              <div className="space-y-3">
                {usersData.users.map((user) => (
                  <div
                    key={user.username}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{user.username}</span>
                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                          {user.role}
                        </Badge>
                        <Badge variant={user.active ? 'success' : 'destructive'}>
                          {user.active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge variant={user.availability_status === 'online' ? 'success' : 'secondary'}>
                          {user.availability_status === 'online' ? 'Online' : 'Offline'}
                        </Badge>
                      </div>
                      {!!Object.keys(user.permissions || {}).length && user.role !== 'admin' && (
                        <p className="text-xs text-muted-foreground">
                          Permissions: {Object.keys(user.permissions || {}).join(', ')}
                        </p>
                      )}
                      {user.pushover_user_key && (
                        <p className="text-xs text-muted-foreground">
                          Pushover configured
                        </p>
                      )}
                      {!!user.telegram_accounts?.length && (
                        <div className="space-y-0.5 text-xs text-muted-foreground">
                          {user.telegram_accounts.map((account) => (
                            <p key={account.user_id}>
                              Telegram: {account.username ? `@${account.username}` : account.display_name || 'account'}
                              {' · '}ID {account.user_id}
                              {account.is_primary && <span className="ml-1 text-green-500">(primary ✓)</span>}
                            </p>
                          ))}
                        </div>
                      )}
                      {telegramNicknamesForUser(user).length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          AKA: {telegramNicknamesForUser(user).map((nick) => `@${nick}`).join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <MergeTelegramUserDialog
                        source={user}
                        users={usersData.users}
                        onMerged={(target) => {
                          setErrorMessage('')
                          setSuccessMessage(`Merged ${user.username} into ${target.username}`)
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEditDialog(user)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDeleteDialog(user)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to the system
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-username">Username</Label>
              <Input
                id="create-username"
                placeholder="Enter username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-password">Password</Label>
              <Input
                id="create-password"
                type="password"
                placeholder="Enter password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-role">Role</Label>
              <Select value={newRole} onValueChange={(v) => setNewRole(v as 'user' | 'admin')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="create-active">Active</Label>
              <Switch
                id="create-active"
                checked={newActive}
                onCheckedChange={setNewActive}
              />
            </div>
            <div className="space-y-2">
              <Label>Permissions</Label>
              {newRole === 'admin' ? (
                <p className="text-xs text-muted-foreground">
                  Admin users automatically have all permissions.
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {PERMISSION_OPTIONS.map((perm) => (
                    <div key={perm.key} className="flex items-center justify-between gap-2 rounded-md border p-2">
                      <span className="text-sm">{perm.label}</span>
                      <Switch
                        checked={!!newPermissions[perm.key]}
                        onCheckedChange={(checked) =>
                          setNewPermissions((prev) => ({ ...prev, [perm.key]: checked }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-pushover">Pushover User Key (optional)</Label>
              <Input
                id="create-pushover"
                placeholder="Enter Pushover user key"
                value={newPushoverKey}
                onChange={(e) => setNewPushoverKey(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-telegram-nickname">Telegram Nicknames (optional)</Label>
              <Textarea
                id="create-telegram-nickname"
                aria-describedby="create-telegram-nickname-help"
                placeholder="main_account, alt_account"
                value={newTelegramNickname}
                onChange={(e) => setNewTelegramNickname(e.target.value)}
                rows={3}
              />
              <p id="create-telegram-nickname-help" className="text-xs text-muted-foreground">
                A uniquely assigned tag links this account on the next Telegram sign-in. No bot chat is required.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={createUser.isPending}>
              {createUser.isPending ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user "{selectedUser?.username}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-password">New Password (leave blank to keep current)</Label>
              <Input
                id="edit-password"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select value={newRole} onValueChange={(v) => setNewRole(v as 'user' | 'admin')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-active">Active</Label>
              <Switch
                id="edit-active"
                checked={newActive}
                onCheckedChange={setNewActive}
              />
            </div>
            <div className="space-y-2">
              <Label>Permissions</Label>
              {newRole === 'admin' ? (
                <p className="text-xs text-muted-foreground">
                  Admin users automatically have all permissions.
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {PERMISSION_OPTIONS.map((perm) => (
                    <div key={perm.key} className="flex items-center justify-between gap-2 rounded-md border p-2">
                      <span className="text-sm">{perm.label}</span>
                      <Switch
                        checked={!!newPermissions[perm.key]}
                        onCheckedChange={(checked) =>
                          setNewPermissions((prev) => ({ ...prev, [perm.key]: checked }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-pushover">Pushover User Key</Label>
              <Input
                id="edit-pushover"
                placeholder="Enter Pushover user key"
                value={newPushoverKey}
                onChange={(e) => setNewPushoverKey(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-telegram-nickname">Telegram Nicknames</Label>
              <Textarea
                id="edit-telegram-nickname"
                aria-describedby="edit-telegram-nickname-help"
                placeholder="main_account, alt_account"
                value={newTelegramNickname}
                onChange={(e) => setNewTelegramNickname(e.target.value)}
                rows={3}
              />
              <p id="edit-telegram-nickname-help" className="text-xs text-muted-foreground">
                A uniquely assigned tag links this account on the next Telegram sign-in. No bot chat is required.
              </p>
            </div>
            {selectedUser && !selectedUser.telegram_accounts?.length && telegramChatIdsForUser(selectedUser).length > 0 && (
              <div className="space-y-2">
                <Label>Telegram Chat IDs</Label>
                <Input
                  value={telegramChatIdsForUser(selectedUser).join(', ')}
                  disabled
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Legacy notification destinations; not valid for command authorization
                </p>
              </div>
            )}
            {!!selectedUser?.telegram_accounts?.length && (
              <div className="space-y-2">
                <Label>Verified Telegram Accounts</Label>
                {selectedUser.telegram_accounts.map((account) => (
                  <div key={account.user_id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                    <div className="min-w-0 text-sm">
                      <p className="font-medium">
                        {account.username ? `@${account.username}` : account.display_name || 'Telegram account'}
                        {account.is_primary && <span className="ml-2 text-xs text-green-500">Primary</span>}
                      </p>
                      <p className="font-mono text-xs text-muted-foreground">ID {account.user_id}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleAdminUnlinkTelegram(selectedUser, account.user_id)}
                      title="Unlink Telegram account"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUser} disabled={updateUser.isPending}>
              {updateUser.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete user "{selectedUser?.username}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={deleteUser.isPending}>
              {deleteUser.isPending ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  )
}
